import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  DEFAULT_DASHBOARD_THEME,
  isDashboardThemeId,
  type DashboardThemeId,
} from "./registry";

/**
 * Which look the back office wears, and how it reaches the page.
 *
 * The same shape as `usePosTheme`, for the same reasons. The choice is
 * persisted PER DEVICE: the storefront theme is the restaurant's public face
 * and lives on the server, but the dashboard look is the working environment
 * of whoever is at this screen, and a till in the kitchen doorway and the
 * owner's laptop are allowed to differ. A per-store setting would also need a
 * column, a DTO and a permission before anyone had seen a second look; this
 * can grow into one once there is a reason.
 *
 * It is applied as an ATTRIBUTE ON `<body>`, not a class on the shell's own
 * element, for the reason `usePosTheme` records: every modal and toast portals
 * to `document.body`, and a scope on the shell would leave them in the default
 * look — a Trioty dialog over a STACK page. The theme files select on
 * `body[data-dashboard-theme="…"]`. The light till mounts the hook (its
 * stylesheets yield to `.pos-dark`), and so does the kitchen board.
 *
 * A tiny external store rather than component state, so the appearance page
 * and the shell that applies the attribute agree without a context to thread
 * through — the page writes, the shell re-renders.
 */
const STORAGE_KEY = "triotyDashboardTheme";

const listeners = new Set<() => void>();

function read(): DashboardThemeId {
  // Wrapped: a private window, cleared site data, or a browser set to block
  // storage all throw on access rather than returning null.
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isDashboardThemeId(value) ? value : DEFAULT_DASHBOARD_THEME;
  } catch {
    return DEFAULT_DASHBOARD_THEME;
  }
}

let current: DashboardThemeId = read();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): DashboardThemeId {
  return current;
}

export function setDashboardTheme(next: DashboardThemeId): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // A screen that cannot persist the choice still honours it for this
    // session; losing the preference is not worth failing the tap.
  }
  listeners.forEach((listener) => listener());
}

/** The current look, and a setter. Re-renders every subscriber on change. */
export function useDashboardTheme(): {
  theme: DashboardThemeId;
  setTheme: (next: DashboardThemeId) => void;
} {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setTheme = useCallback((next: DashboardThemeId) => {
    setDashboardTheme(next);
  }, []);
  return { theme, setTheme };
}

/**
 * Puts the look on `<body>` for as long as the caller is mounted.
 *
 * Called by `ManageShell`, and by the till's `Screen` for the light till —
 * the cashier works in the same look the back office wears. The look's
 * stylesheets yield to `.pos-dark`, so the dark till stays the till's own
 * night room. Removed on unmount so the attribute never outlives its screen.
 */
export function useApplyDashboardTheme(enabled = true): void {
  const { theme } = useDashboardTheme();
  useEffect(() => {
    if (!enabled) return;
    const { body } = document;
    body.dataset.dashboardTheme = theme;
    return () => {
      delete body.dataset.dashboardTheme;
    };
  }, [theme, enabled]);
}
